import React, { useEffect, useState } from "react";
import { Button, Select, Switch, Table, Tooltip } from "antd";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { useScopeParams } from "@/hooks/useScopeParams";
import { CheckOutlined, CloseOutlined, MinusOutlined } from "@ant-design/icons";
import AlertIcon from "@/assets/icons/alert_icon.svg?react";
import CalendarMonthIcon from "@/assets/icons/calendar_month.svg?react";
import { getColumnConfig } from "@/views/Subjects/Activities/VAColumnConfig";
import { drawerTableColumns } from "@/views/Subjects/Activities/DrawerColumConfig";
import {
  getActivitiesOptions,
  getGroupArray,
  getLastKeyValue,
  toTitleCase,
} from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import TooltipTag from "@/components/UI/TooltipTag";
import {
  pillColor,
  textColor,
  tooltipTitle,
} from "@/utils/tableConfigs/VAStatusMapping";
import { UNSCHEDULED_VISIT_PREFIX } from "@/utils/unscheduledVisitUtils/unscheduledVisitUtils";
import Error from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import { useFetch } from "@/hooks/FetchHook";
import Loader from "@/components/UI/Loader";
import { normalizeText } from "@/utils/tableConfigs/GlobalFunctions";

type Props = { siteId: string };

const tableLegend = [
  {
    title: "All Activities Completed",
    icon: (
      <CheckOutlined
        style={{ color: "#06709B", backgroundColor: "#D2EFF9", padding: "2px" }}
      />
    ),
    tooltip: "All expected activities were completed at the visit timepoint.",
  },
  {
    title: "Some Activities Completed",
    icon: (
      <div className="flex items-center justify-center bg-[#FAEEDF]">
        <AlertIcon />
      </div>
    ),
    tooltip:
      "Some, but not all, expected activities were completed at a visit timepoint.",
  },
  {
    title: "No Activities Completed",
    icon: (
      <CloseOutlined
        style={{
          color: "#73134A",
          backgroundColor: "#EBD1E0",
          padding: "2px",
        }}
      />
    ),
    tooltip: "No activities were completed at this time point",
  },
  {
    title: "No Data",
    icon: (
      <MinusOutlined
        style={{
          color: "#D5DADC",
          backgroundColor: "white",
          border: "1px solid #D5DADC",
          padding: "2px",
          borderRadius: "2px",
        }}
      />
    ),
    tooltip: "No data or activity not required.",
  },
];

export default function CRFCompletionTab({ siteId }: Props) {
  const { scope, id } = useScopeParams();
  const path = `/${scope}/${id}/subjects/overview`;

  const [selectedActivities, setSelectedActivities] = useState("all");
  const [selectedCell, setSelectedCell] = useState<any>(null);
  const [showCount, setShowCount] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 400);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [columnToHide, setColumnToHide] = useState(UNSCHEDULED_VISIT_PREFIX);
  const [tableKey, setTableKey] = useState(0);
  // Same contract as the trial-wide Visits & Activities grid: open on enrolled
  // subjects, and let "Clear all filters" turn that off. See VAColumnConfig.
  const [applyDefaultFilters, setApplyDefaultFilters] = useState(true);

  const {
    data: allData,
    isLoading: loadingCount,
    isError: isErrorCount,
    error: errorObjCount,
  } = useFetch({
    endpointSuffix: `activities/by-count-completion/${
      selectedActivities === "all" ? "" : selectedActivities
    }`,
  });

  const {
    data: activityDictionary,
    isError: isErrorActivity,
    error: errorObjActivity,
  } = useFetch({ endpointSuffix: "activities/" });

  // Filter to this site only
  const countData = Array.isArray(allData)
    ? allData.filter((r) => r.site === siteId)
    : [];

  const isError = isErrorCount || isErrorActivity;
  const errorObj = errorObjCount || errorObjActivity;

  const customOrder = [
    "VISIT",
    "SCREENING",
    "INTERVENTION",
    "PHYSICAL EVALUATION",
    "LABS",
    "IMAGING",
    "QUESTIONNAIRE",
  ];

  const activityCategoryList = Array.isArray(activityDictionary)
    ? activityDictionary
        .map((obj) => Object.keys(obj)[0])
        .sort((a, b) => {
          const indexA = customOrder.indexOf(a);
          const indexB = customOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
    : [];

  const sortedActivityDictionary = sortActivityDictionary(activityDictionary);
  const optionsActivities = getActivitiesOptions(sortedActivityDictionary);

  const handleCellClick = (columnName: any, value: any, record: any) => {
    setSelectedCell({
      subject: record.subject,
      site: record.site,
      status: record.study_status,
      [columnName]: value,
    });
    setOpenDrawer(true);
  };

  const visitType = getLastKeyValue(selectedCell ?? {});

  const countColumns = (type: string): any[] =>
    countData.length > 0
      ? (getColumnConfig(
          countData,
          handleCellClick,
          columnToHide,
          path,
          type,
          id,
          applyDefaultFilters,
        ) as any[])
      : [];

  useEffect(() => {
    const handleResize = () => setTableHeight(window.innerHeight - 390);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelectActivities = (selected: any) => {
    setSelectedActivities(selected);
    setTableKey((k) => k + 1);
  };

  const handleClearAll = () => {
    setSelectedActivities("all");
    setApplyDefaultFilters(false);
    setTableKey((k) => k + 1);
  };

  if (isError) {
    if ((errorObj as any)?.statusCode === 403) {
      return (
        <div className="flex mt-25 justify-center items-center">
          <Error403 />
        </div>
      );
    }
    return <Error />;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-4">
          <Tooltip title="Select a single activity by which to filter the table.">
            <Select
              className="customSelect shadow-xs"
              value={selectedActivities}
              onChange={handleSelectActivities}
              placeholder="Activity: ALL"
              options={optionsActivities}
              style={{ width: 280 }}
              labelRender={({ label }) => <span>Activity: {label}</span>}
            />
          </Tooltip>
          <Button type="text" icon={<CloseOutlined />} onClick={handleClearAll}>
            Clear all filters
          </Button>
        </div>
        <div className="flex">
          <div className="flex gap-3 items-center">
            <p className={showCount ? "text-[#556C75]" : "text-[black]"}>
              Show completion
            </p>
            <Tooltip title="Show high level completion of all activities at a visit or a break down of activity counts per visit.">
              <Switch onChange={(c) => setShowCount(c)} value={showCount} />
            </Tooltip>
            <p className={showCount ? "text-[black]" : "text-[#556C75]"}>
              Show count
            </p>
          </div>
          <p className="text-[#556C75] mx-3">|</p>
          <div className="flex items-center gap-3">
            <p className={showUnscheduled ? "text-[black]" : "text-[#556C75]"}>
              Show Unscheduled Visits
            </p>
            <Switch
              checked={showUnscheduled}
              onChange={(c) => {
                setShowUnscheduled(c);
                setColumnToHide(c ? "" : UNSCHEDULED_VISIT_PREFIX);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex bg-white items-center p-2 gap-4 rounded-sm mb-2 shadow-xs">
        {tableLegend.map((legend) => (
          <Tooltip title={legend.tooltip} key={legend.title}>
            <div className="flex gap-3 items-center">
              {legend.icon}
              <p>{legend.title}</p>
            </div>
          </Tooltip>
        ))}
      </div>

      <Table
        key={`${tableKey}-${countData.length > 0 ? "rows" : "empty"}`}
        bordered
        className="shadow-md"
        columns={countColumns(showCount ? "count" : "completion")}
        dataSource={countData}
        loading={{
          indicator: (
            <div className="flex justify-center w-full h-full items-center">
              <Loader noMargin tableLoader />
            </div>
          ),
          spinning: loadingCount,
        }}
        rowKey={(record) => record.participant_id}
        scroll={{ x: 1500, y: tableHeight }}
        size="small"
        pagination={false}
        virtual={true}
        footer={(displayData) => (
          <div className="flex gap-4">
            <p className="font-bold">Total records: {countData.length}</p>
            {displayData?.length !== countData.length && (
              <p className="font-bold">
                Selected records: {displayData?.length}
              </p>
            )}
          </div>
        )}
      />

      <SiteSubjectDrawer
        selectedCell={selectedCell}
        visitType={visitType}
        activityCategoryList={activityCategoryList}
        drawerOpen={openDrawer}
        setDrawerOpen={setOpenDrawer}
      />
    </div>
  );
}

function SiteSubjectDrawer({
  selectedCell,
  visitType,
  activityCategoryList,
  drawerOpen,
  setDrawerOpen,
}: any) {
  const encodedVisitType = encodeURIComponent(visitType.key);

  const { data, isLoading, isError } = useFetch({
    endpointSuffix: `activities/${selectedCell?.subject}/?timepoint=${encodedVisitType}`,
    useQueryOptions: { enabled: drawerOpen } as any,
  });

  const subjectByActivityCategory = getGroupArray(
    data as any,
    activityCategoryList,
    "activity_type",
  );

  const visitDate = subjectByActivityCategory?.map((category) => {
    const title = Object.keys(category)[0];
    const items = category[title];
    return title === "VISIT" ? items[0]?.date : "";
  });

  const allRequired = subjectByActivityCategory?.filter((category) => {
    const title = Object.keys(category)[0];
    const items = category[title];
    return (
      items &&
      items.length > 0 &&
      items.some((item) => item.activity_status !== "Not Required")
    );
  });

  const allNotRequired = subjectByActivityCategory?.every((category) => {
    const title = Object.keys(category)[0];
    const items = category[title];
    return items.every((item) => item.activity_status === "Not Required");
  });

  const studyStatus = normalizeText(selectedCell?.status) || "-";

  return (
    <ResizableDrawer
      headerTitle={
        <div className="flex items-center gap-2">
          <h1 className="text-white">Subject {selectedCell?.subject}</h1>
          <TooltipTag
            tagColor={pillColor[studyStatus]}
            textColor={textColor[studyStatus]}
            tooltipTitle={tooltipTitle[studyStatus]}
            tagStyle={{
              padding: "5px 20px",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {studyStatus}
          </TooltipTag>
        </div>
      }
      headersArray={[
        { key: "SITE", value: selectedCell?.site || "-" },
        { key: "TIMEPOINT", value: visitType.key || "-" },
        { key: "DATE", value: visitDate || "" },
      ]}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sizeToggle={{
        collapsedWidth: 750,
        expandedWidth: 1250,
        initialWidth: 750,
      }}
      destroyOnHidden={true}
      closable={false}
      closeIcon={null}
    >
      <>
        {isError ? (
          <Error />
        ) : isLoading ? (
          <div className="flex mt-5 justify-center items-center">
            <Loader />
          </div>
        ) : (
          <>
            {allRequired?.map((category) => {
              const title = Object.keys(category)[0];
              const items = category[title];
              return (
                <div key={title} className="mb-7">
                  <h1 className="font-bold mb-2">{toTitleCase(title)}</h1>
                  <Table
                    columns={drawerTableColumns as any}
                    rowKey={(record: any) => record.activity}
                    dataSource={items as any[]}
                    pagination={false}
                  />
                </div>
              );
            })}
            {allNotRequired && (
              <div className="flex flex-col items-center justify-center mt-5 mb-3">
                <CalendarMonthIcon />
                <p className="text-[#6e6c6c]">Visit Not Required</p>
              </div>
            )}
          </>
        )}
      </>
    </ResizableDrawer>
  );
}

function sortActivityDictionary(activityDictionary: any) {
  if (!Array.isArray(activityDictionary)) return [];
  return activityDictionary
    .map((obj) => {
      const key = Object.keys(obj)[0];
      return { [key]: obj[key].sort() };
    })
    .sort((a, b) => {
      const keyA = Object.keys(a)[0].toUpperCase();
      const keyB = Object.keys(b)[0].toUpperCase();
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
}
